import { useState, useRef, useCallback } from "react";

/**
 * useTourController — controlled state for a tour that can pause, hand off to a
 * second flow (e.g. an in-drawer tour), then resume where it left off.
 *
 * Own a tour's visibility + current step here and drive an SbTour with the
 * returned `open` / `current` / `remountKey`:
 *
 *   const tour = useTourController();
 *   <SbTour key={tour.remountKey} open={tour.open} current={tour.current} onChange={tour.goToStep} ... />
 *
 * Flow helpers:
 *   - begin()            : (re)start from step 0
 *   - goToStep(step)     : move to a step (wire to the Tour's onChange)
 *   - close()            : hide the tour
 *   - pause(resumeAt)    : hide the tour, remembering where to resume
 *   - resume()           : re-open at the remembered step
 *
 * `remountKey` bumps on begin/resume so the Tour remounts fresh — a pause→resume
 * (open false→true) can otherwise leave AntD's motion stuck (card visible but
 * buttons dead). Remounting sidesteps that.
 */
export default function useTourController() {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState(0);
  const [remountKey, setRemountKey] = useState(0);
  const resumeAtRef = useRef(0);

  // Open the tour, optionally at a specific step (defaults to the first). Passing
  // a step is handy for resuming after a cross-page hop, where the in-memory
  // resume point is lost on unmount.
  const begin = useCallback((startStep = 0) => {
    setCurrent(startStep);
    setRemountKey((key) => key + 1);
    setOpen(true);
  }, []);

  const goToStep = useCallback((step) => setCurrent(step), []);

  const close = useCallback(() => setOpen(false), []);

  const pause = useCallback((resumeAt) => {
    resumeAtRef.current = resumeAt;
    setOpen(false);
  }, []);

  const resume = useCallback(() => {
    setCurrent(resumeAtRef.current);
    setRemountKey((key) => key + 1);
    setOpen(true);
  }, []);

  return { open, current, remountKey, begin, goToStep, close, pause, resume };
}
